import axios from "axios";
import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Row,
  Col,
  Dropdown,
  Pagination,
  Spinner,
  Card,
} from "react-bootstrap";
import { FaSearch, FaTimes, FaFilter } from "react-icons/fa";
import { sidebarMenuItems } from "../../data/Menudata.jsx";
import { showMyAlert } from "../../Utils/alertService.js";
import { doAuditLog } from "../../Utils/saveHistoryAction.js";

const UserAuthentication = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    let filtered = [...users];

    if (roleFilter !== "all") {
      filtered = filtered.filter((user) => user.role === roleFilter);
    }

    if (statusFilter !== "all") {
      filtered = filtered.filter((user) => user.status === statusFilter);
    }

    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter((user) => {
        return (
          user.fullName?.toLowerCase().includes(searchLower) ||
          user.email?.toLowerCase().includes(searchLower) ||
          user.mobileNumber?.toLowerCase().includes(searchLower) ||
          user.role?.toLowerCase().includes(searchLower) ||
          user.status?.toLowerCase().includes(searchLower) ||
          user.createdDate?.toLowerCase().includes(searchLower)
        );
      });
    }

    setFilteredUsers(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  }, [users, searchTerm, roleFilter, statusFilter]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const sessionCompanyKey = sessionStorage.getItem("companyKey");
      const res = await axios.get(
        "https://api.dremerz.net/api/DZ_8922_Payroll_PayrollCreateAdmin_PayrollAdminList"
      );
      const filteredData = res.data.filter(
        (data) => data.companyKey === sessionCompanyKey
      );
      setUsers(filteredData.reverse());
    } catch (error) {
      console.log("Error fetching users data:", error);
    } finally {
      setLoading(false);
    }
  };

  const getAllMenuItems = (menuItems) => {
    const flatItems = [];

    menuItems.forEach((item) => {
      if (item.hasSubmenu && item.children) {
        // Add parent item
        flatItems.push({
          id: item.id,
          pageName: item.id,
          menuName: item.title,
          isParent: true,
        });
        // Add children
        item.children.forEach((child) => {
          flatItems.push({
            id: child.id,
            pageName: child.id,
            menuName: child.title,
            parentId: item.id,
          });
        });
      } else {
        flatItems.push({
          id: item.id,
          pageName: item.id,
          menuName: item.title,
        });
      }
    });

    return flatItems;
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const clearSearch = () => {
    setSearchTerm("");
  };

  const clearFilters = () => {
    setRoleFilter("all");
    setStatusFilter("all");
    setSearchTerm("");
  };

  const handleOpenPermissionModal = (user) => {
    setSelectedUser(user);
    // Parse existing permissions or initialize empty array
    const existingPermissions = user.permissions
      ? user.permissions.split(",")
      : [];
    setSelectedPermissions(existingPermissions);
    setShowPermissionModal(true);
  };

  const handleClosePermissionModal = () => {
    setShowPermissionModal(false);
    setSelectedUser(null);
    setSelectedPermissions([]);
  };

  const handlePermissionToggle = (pageName) => {
    setSelectedPermissions((prev) => {
      if (prev.includes(pageName)) {
        return prev.filter((name) => name !== pageName);
      } else {
        return [...prev, pageName];
      }
    });
  };

  const handleUpdatePermissions = async () => {
    if (!selectedUser) return;

    try {
      setUpdateLoading(true);
      const permissionsString = selectedPermissions.join(",");

      // Update user permissions via API
      await axios.put(
        `https://api.dremerz.net/api/DZ_8922_Payroll_PayrollCreateAdmin_PayrollAdminList/${selectedUser.id}`,
        {
          ...selectedUser,
          permissions: permissionsString,
        }
      );
      doAuditLog("Update", "User Authentication", selectedUser, {
        ...selectedUser,
        permissions: permissionsString,
      });
      showMyAlert({
        type: "success", // "success" | "danger" | "warning" | "info"
        title: "Updated",
        message: "Permission Updated Successfully",
        duration: 2500, // auto-close in 2.5s
      });

      // Update local state
      setUsers((prevUsers) =>
        prevUsers.map((user) =>
          user.id === selectedUser.id
            ? { ...user, permissions: permissionsString }
            : user
        )
      );

      handleClosePermissionModal();
      await fetchUsers(); // Refresh data
    } catch (error) {
      console.log("Error updating permissions:", error);
    } finally {
      setUpdateLoading(false);
    }
  };

  // Pagination calculations
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleItemsPerPageChange = (count) => {
    setItemsPerPage(count);
    setCurrentPage(1);
  };

  if (loading) {
    return (
      <div className="text-center mt-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <div className="p-4">
      {/* Header Section */}
      <div className="row align-items-center mb-2 border rounded bg-light py-2">
        <div className="col-12 col-md-6">
          <h5 className="mb-0 d-flex align-items-center flex-start">
            User Authentication
          </h5>
        </div>
      </div>

      {/* Search and Filter Section */}
      <div className="row align-items-center mb-3 border rounded bg-light py-2">
        {/* Search input */}
        <div className="col-12 col-md-4 mb-2 mb-md-0">
          <div className="input-group input-group-sm">
            <span className="input-group-text bg-light border-end-0">
              <FaSearch className="text-muted" />
            </span>
            <input
              type="text"
              className="form-control"
              placeholder="Search here..."
              value={searchTerm}
              onChange={handleSearchChange}
            />
            {searchTerm && (
              <button
                className="btn btn-outline-secondary"
                type="button"
                onClick={clearSearch}
              >
                <FaTimes />
              </button>
            )}
          </div>
        </div>

        {/* Filter dropdowns */}
        <div className="col-12 col-md-8">
          <div className="d-flex flex-wrap justify-content-center justify-content-md-end gap-2">
            <Dropdown>
              <Dropdown.Toggle
                size="sm"
                variant="outline-secondary"
                id="role-filter"
              >
                <FaFilter className="me-2" />
                {roleFilter === "all" ? "All Roles" : roleFilter}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item onClick={() => setRoleFilter("all")}>
                  All Roles
                </Dropdown.Item>
                <Dropdown.Item onClick={() => setRoleFilter("superadmin")}>
                  Super Admin
                </Dropdown.Item>
                <Dropdown.Item onClick={() => setRoleFilter("manager")}>
                  Manager
                </Dropdown.Item>
                <Dropdown.Item onClick={() => setRoleFilter("admin")}>
                  Admin
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>

            <Dropdown>
              <Dropdown.Toggle
                size="sm"
                variant="outline-secondary"
                id="status-filter"
              >
                <FaFilter className="me-2" />
                {statusFilter === "all" ? "All Status" : statusFilter}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item onClick={() => setStatusFilter("all")}>
                  All Status
                </Dropdown.Item>
                <Dropdown.Item onClick={() => setStatusFilter("active")}>
                  Active
                </Dropdown.Item>
                <Dropdown.Item onClick={() => setStatusFilter("inactive")}>
                  Inactive
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>

            <button
              className="btn btn-outline-danger btn-sm"
              onClick={clearFilters}
              disabled={
                roleFilter === "all" && statusFilter === "all" && !searchTerm
              }
            >
              Clear
            </button>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="d-none d-md-block">
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th id="text-white-important">S.I</th>
              <th id="text-white-important">Full Name</th>
              <th id="text-white-important">Email</th>
              <th id="text-white-important">Mobile Number</th>
              <th id="text-white-important">Role</th>
              <th id="text-white-important">Status</th>
              {sessionStorage.getItem("role") === "superadmin" && (
                <th id="text-white-important">Permissions</th>
              )}
            </tr>
          </thead>
          <tbody>
            {currentUsers.length > 0 ? (
              currentUsers.map((user, index) => (
                <tr key={user.id || index}>
                  <td>{filteredUsers.length - (indexOfFirstItem + index)}</td>
                  {/* <td>{indexOfFirstItem + index + 1}</td> */}

                  <td>{user.fullName}</td>
                  <td>{user.email}</td>
                  <td>{user.mobileNumber}</td>
                  <td>{user.role}</td>
                  <td>{user.status}</td>
                  {sessionStorage.getItem("role") === "superadmin" && (
                    <td>
                      <Button
                        variant="success"
                        size="sm"
                        onClick={() => handleOpenPermissionModal(user)}
                      >
                        Manage Permission
                      </Button>
                    </td>
                  )}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8" className="text-center text-muted py-4">
                  {searchTerm || roleFilter !== "all" || statusFilter !== "all"
                    ? "No users found matching the current filters"
                    : "No users found"}
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {/* Results Count */}
      {filteredUsers.length > 0 && (
        <div className="col-12 text-end">
          <span className="text-muted small">
            Showing {indexOfFirstItem + 1} to{" "}
            {Math.min(indexOfLastItem, filteredUsers.length)} of{" "}
            {filteredUsers.length} users
          </span>
        </div>
      )}

      <div className="d-block d-md-none">
        {currentUsers.length > 0 ? (
          currentUsers.map((user, index) => (
            <Card key={user.id || index} className="mb-3 shadow-sm border">
              <Card.Body className="p-2">
                <Table bordered size="sm" className="mb-2">
                  <tbody>
                    <tr>
                      <td className="fw-bold" style={{ width: "40%" }}>
                        SI
                      </td>
                      <td>
                        {filteredUsers.length - (indexOfFirstItem + index)}
                      </td>
                    </tr>
                    <tr>
                      <td className="fw-bold">Full Name</td>
                      <td>{user.fullName}</td>
                    </tr>
                    <tr>
                      <td className="fw-bold">Email</td>
                      <td>{user.email}</td>
                    </tr>
                    <tr>
                      <td className="fw-bold">Mobile</td>
                      <td>{user.mobileNumber}</td>
                    </tr>
                    <tr>
                      <td className="fw-bold">Role</td>
                      <td>{user.role}</td>
                    </tr>
                    <tr>
                      <td className="fw-bold">Status</td>
                      <td>{user.status}</td>
                    </tr>
                    {sessionStorage.getItem("role") === "superadmin" && (
                      <tr>
                        <td className="fw-bold"></td>
                        <td>
                          <Button
                            variant="outline-success"
                            size="sm"
                            className="w-100"
                            onClick={() => handleOpenPermissionModal(user)}
                          >
                            Manage
                          </Button>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          ))
        ) : (
          <div className="text-center text-muted py-3">No users found</div>
        )}
      </div>

      {/* Pagination Controls */}
      {filteredUsers.length > 0 && (
        <div className="row align-items-center justify-content-between mb-2 p-2">
          <div className="col-5 col-md-auto">
            <div className="d-flex align-items-center gap-2">
              <span className="text-muted small">Paginate</span>
              <Dropdown>
                <Dropdown.Toggle
                  size="sm"
                  variant="outline-secondary"
                  id="items-per-page-dropdown"
                >
                  {itemsPerPage}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {[5, 10, 20, 50, 100].map((count) => (
                    <Dropdown.Item
                      key={count}
                      onClick={() => handleItemsPerPageChange(count)}
                      active={itemsPerPage === count}
                    >
                      {count}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </div>

          <div className="col-7 col-md-auto">
            <Pagination
              size="sm"
              className="mb-0 justify-content-center justify-content-md-end"
            >
              <Pagination.First
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
              />
              <Pagination.Prev
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              />

              {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                (page) => (
                  <Pagination.Item
                    key={page}
                    active={page === currentPage}
                    onClick={() => handlePageChange(page)}
                  >
                    {page}
                  </Pagination.Item>
                )
              )}

              <Pagination.Next
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              />
              <Pagination.Last
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
              />
            </Pagination>
          </div>
        </div>
      )}

      {/* Permissions Modal */}
      <Modal
        show={showPermissionModal}
        onHide={handleClosePermissionModal}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Update Permissions for {selectedUser?.fullName}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
          <Row>
            {sidebarMenuItems.map((menuItem) => (
              <React.Fragment key={menuItem.id}>
                {/* Parent Menu Item */}
                {!menuItem.hasSubmenu && (
                  <Col md={6} className="mb-3">
                    <div className="d-flex align-items-center">
                      {/* <menuItem.icon className="me-2" /> */}
                      <Form.Check
                        type="checkbox"
                        id={`page-${menuItem.id}`}
                        label={menuItem.title}
                        checked={selectedPermissions.includes(menuItem.id)}
                        onChange={() => handlePermissionToggle(menuItem.id)}
                        className="mb-0"
                      />
                    </div>
                  </Col>
                )}

                {/* Parent Menu Item with Children */}
                {menuItem.hasSubmenu && (
                  <Col md={12} className="mb-3">
                    <Card className="border-light">
                      <Card.Header className="bg-light py-2">
                        <div className="d-flex align-items-center">
                          <menuItem.icon className="me-2" />
                          <strong>{menuItem.title}</strong>
                        </div>
                      </Card.Header>
                      <Card.Body className="py-2">
                        <Row>
                          {menuItem.children.map((child) => (
                            <Col md={6} key={child.id} className="mb-2">
                              <div className="d-flex align-items-center">
                                {/* <child.icon className="me-2 text-muted" /> */}
                                <Form.Check
                                  type="checkbox"
                                  id={`page-${child.id}`}
                                  label={child.title}
                                  checked={selectedPermissions.includes(
                                    child.id
                                  )}
                                  onChange={() =>
                                    handlePermissionToggle(child.id)
                                  }
                                  className="mb-0"
                                />
                              </div>
                            </Col>
                          ))}
                        </Row>
                      </Card.Body>
                    </Card>
                  </Col>
                )}
              </React.Fragment>
            ))}
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClosePermissionModal}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleUpdatePermissions}
            disabled={updateLoading}
          >
            {updateLoading ? <Spinner size="sm" /> : "Update Permissions"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default UserAuthentication;
